import { FC, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { buildFallbackTldrText, buildScopeTitle, getTopRisingPatterns } from '../../helpers/showcase'
import { useMonthLabel } from '../../hooks/useMonthLabel'
import { useUAInsightsDashboard } from '../../hooks/useUAInsightsDashboard'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { UAInsightsNavigationProvider, useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUAInsightsShowcase } from '../../hooks/useUAInsightsShowcase'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeMotivationsChart } from '../CreativeMotivationsChart/CreativeMotivationsChart'
import { DashboardSkeleton } from '../DashboardSkeleton/DashboardSkeleton'
import { EmptyStateCard } from '../EmptyStateCard/EmptyStateCard'
import { MonthlyTldrCard } from '../MonthlyTldrCard/MonthlyTldrCard'
import { MotivationDetailView } from '../MotivationDetailView/MotivationDetailView'
import { RisingStoryPatterns } from '../RisingStoryPatterns/RisingStoryPatterns'
import { SectionCard } from '../SectionCard/SectionCard'
import type { ShowcaseCrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { ShowcaseBreadcrumb } from '../ShowcaseBreadcrumb/ShowcaseBreadcrumb'
import { StoryPatternDetailView } from '../StoryPatternDetailView/StoryPatternDetailView'
import { StoryPatternsTable } from '../StoryPatternsTable/StoryPatternsTable'
import { UAInsightsDialogs } from '../UAInsightsDialogs/UAInsightsDialogs'
import { UAInsightsErrorState } from '../UAInsightsErrorState/UAInsightsErrorState'
import { UAInsightsHeaderBar } from '../UAInsightsHeaderBar/UAInsightsHeaderBar'
import { UAInsightsSummaryCards } from '../UAInsightsSummaryCards/UAInsightsSummaryCards'

export const UAInsightsDashboard: FC = () => (
  <UAInsightsNavigationProvider>
    <UAInsightsDashboardContent />
  </UAInsightsNavigationProvider>
)

const UAInsightsDashboardContent: FC = () => {
  const { t } = useTranslation('ua-insights')
  const taxonomy = useUATaxonomy()
  const { patternLabel, motivationLabel } = taxonomy
  const { motivationById, patternById } = useUAInsightsMeta()
  const { data: dashboardData, isLoading: isDashboardLoading, isError: isDashboardError, refetch: refetchDashboard } = useUAInsightsDashboard()
  const { data: showcase, isLoading: isShowcaseLoading, isError: isShowcaseError, refetch: refetchShowcase } = useUAInsightsShowcase()
  const { params, month, backToOverview, openGame, openGenreTags, openAllCreatives, openAllGames, openAllTags, openMotivationView, openPatternView } =
    useUAInsightsNavigation()

  const formatMonth = useMonthLabel()
  const monthLabel = formatMonth(month)

  const topRising = useMemo(() => (showcase ? getTopRisingPatterns(showcase.narrativePatterns) : []), [showcase])

  // The summary cards describe the whole market scope, so they only belong on
  // the dashboard overview — hide them while a Motivation / Story Pattern
  // drill-down view is open.
  const isDetailView = params.view === 'motivation' || params.view === 'pattern'

  // Breadcrumb for the active drill-down, pinned to the bottom of the sticky
  // header so it stays in view as the user scrolls the detail content. Mirrors
  // the trail the detail views previously rendered inline (lens + parent
  // pattern resolution), built here so it can live in the shared header.
  const detailCrumbs = useMemo<ShowcaseCrumb[] | null>(() => {
    if (!showcase) return null
    const marketCrumb: ShowcaseCrumb = { label: t('showcase_breadcrumb_market'), onClick: backToOverview }
    if (params.view === 'motivation' && params.viewMotivationId) {
      const motivation = motivationById(params.viewMotivationId)
      return [marketCrumb, { label: motivation ? motivationLabel(motivation) : '' }]
    }
    if (params.view === 'pattern' && params.viewPatternId) {
      const lensId = params.viewLensId
      const lens = lensId ? motivationById(lensId) : undefined
      const resolved = patternById(params.viewPatternId)
      const parent = resolved?.parent
      const crumbs: ShowcaseCrumb[] = [marketCrumb]
      if (lensId && lens) crumbs.push({ label: motivationLabel(lens), onClick: () => openMotivationView(lensId) })
      if (parent) crumbs.push({ label: patternLabel(parent), onClick: () => openPatternView(parent.id, lensId) })
      if (resolved) crumbs.push({ label: patternLabel(resolved.pattern) })
      return crumbs
    }
    return null
  }, [
    showcase,
    params.view,
    params.viewMotivationId,
    params.viewPatternId,
    params.viewLensId,
    t,
    patternLabel,
    motivationLabel,
    motivationById,
    patternById,
    backToOverview,
    openMotivationView,
    openPatternView,
  ])

  // Jump back to the top of the page on any view transition — opening or
  // switching a detail view, and returning to the overview via the breadcrumb —
  // so the new view starts in view rather than at the previous scroll position.
  // Deferred across two animation frames so the reset runs after the new view's
  // virtualized tables have laid out and can't be overridden by them.
  useEffect(() => {
    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }))
    })
    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [params.view, params.viewMotivationId, params.viewPatternId])

  // Checked before the loading gate: on a failed dashboard request `isLoading`
  // goes false while `data` stays undefined, so without this branch the gate
  // below would hold forever and leave the user on a skeleton that never
  // resolves.
  if (isDashboardError && !dashboardData) {
    return <UAInsightsErrorState onRetry={refetchDashboard} />
  }

  if (isDashboardLoading || !dashboardData || (isShowcaseLoading && !showcase)) {
    return <DashboardSkeleton />
  }

  const renderBody = () => {
    if (!showcase) {
      return isShowcaseError ? <UAInsightsErrorState onRetry={refetchShowcase} /> : <EmptyStateCard>{t('showcase_load_error')}</EmptyStateCard>
    }

    if (params.view === 'motivation' && params.viewMotivationId) {
      return <MotivationDetailView data={showcase} motivationId={params.viewMotivationId} />
    }
    if (params.view === 'pattern' && params.viewPatternId) {
      return <StoryPatternDetailView patternId={params.viewPatternId} lensMotivationId={params.viewLensId} />
    }

    const tldrTitle = showcase.monthlySummary?.title ?? buildScopeTitle(t, monthLabel)
    const tldrText = showcase.monthlySummary?.text ?? buildFallbackTldrText(t, showcase, taxonomy)

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <MonthlyTldrCard title={tldrTitle} text={tldrText} />

        {topRising.length > 0 && (
          <SectionCard title={t('showcase_top_rising_patterns')}>
            <RisingStoryPatterns patterns={topRising} onSelect={(patternId) => openPatternView(patternId)} />
          </SectionCard>
        )}

        <SectionCard title={t('showcase_creative_motivations')}>
          <CreativeMotivationsChart motivations={showcase.motivations} onSelect={openMotivationView} disableCard />
        </SectionCard>

        <SectionCard title={t('showcase_creative_story_patterns')} disableBodyPadding>
          <StoryPatternsTable
            patterns={showcase.narrativePatterns}
            firstColumnLabel={t('showcase_col_story_pattern')}
            onSelectPattern={(patternId) => openPatternView(patternId)}
            disableCard
          />
        </SectionCard>
      </Box>
    )
  }

  return (
    <Box>
      <UAInsightsHeaderBar
        games={dashboardData.games}
        genres={dashboardData.genres}
        onSelectGame={openGame}
        onSelectGenre={openGenreTags}
        breadcrumb={detailCrumbs ? <ShowcaseBreadcrumb crumbs={detailCrumbs} /> : undefined}
      />

      {!isDetailView && (
        <Box sx={{ mt: 2 }}>
          <UAInsightsSummaryCards
            stats={dashboardData.summaryStats}
            onTotalCreativesClick={openAllCreatives}
            onGamesTrackedClick={openAllGames}
            onUniqueTagsClick={openAllTags}
          />
        </Box>
      )}

      <Box sx={{ mt: 2 }}>{renderBody()}</Box>

      <UAInsightsDialogs />
    </Box>
  )
}
